import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FlightRequest } from '@appTypes/api.types';
import { AirportTreeItem } from '@appTypes/redefined-types';
import { minDate, minDateThanField, requiredIf } from '@core/misc/validators.utils';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { createDispatchMap, createSelectMap } from '@ngxs/store';
import { CHILDRAN_AGES } from '@pages/_common/static-data.const';
import { AVIA_TYPES_STATIC_DATA, NGXS_AVIA_SEARCH_FORM } from '@pages/avia-page/avia-page.const';
import { aviaGetAirports, aviaResetSearchResult, aviaSearchPackages } from '@pages/avia-page/state/avia.actions';
import { AviaState } from '@pages/avia-page/state/avia.state';
import { LoadingWindowService } from '@shared/components/loading-window/loading-window.service';
import { differenceInDays } from 'date-fns';
import { Subject, filter, map, startWith, take } from 'rxjs';
import { appSetCurrency, appSetSellCurrency } from '@app-state/app.actions';
import { AppPersistedState } from '@app-state/app.persisted.state';
import { AppState } from '@app-state/app.state';
import { CatalogueApiService } from '../../../shared/services/api/catalogue-api.service';
import { decorateAirportTree } from '../airport-tree.utils';
import { DEFAULT_CABIN_CLASSES, cabinClassList } from '../avia-cabin-class';
import { FlightTicketTypeValues, TicketTypesEnum } from '../avia-page.types';

@Injectable()
export class AviaSearchFormService {
  //Injectors
  readonly #injector = inject(Injector);
  readonly #loadingWindowService = inject(LoadingWindowService);
  readonly #catalogueApiService = inject(CatalogueApiService);
  //Definitions
  readonly ngxsForm = NGXS_AVIA_SEARCH_FORM;
  readonly today = new Date(new Date().setHours(0, 0, 0, 0));
  /** R-C8: the results page asks the form to change the dates (no fares of the asked class); the form opens / focuses its date. */
  readonly changeDatesRequested = new Subject<void>();
  readonly maxFlightRequests = 4;
  // website-web#10: Round Trip / One Way (hidden since 2024 - a one-way search found nothing until the MyAgent mapper fix of
  // 28.09.2026). Multiple Flights stays off the switch: the MyAgent integration refuses multi-route searches.
  readonly flightTypes = AVIA_TYPES_STATIC_DATA.filter((type) => type.value !== 'multipleFlights');
  readonly childrenAgesData = CHILDRAN_AGES;

  //Signals
  readonly isSubmited = signal<boolean>(false);

  //Selectors
  readonly #selectors = createSelectMap({
    formStatus: AviaState.searchLoadStatus,
    searchFormData: AviaState.searchFormData,
    airports: AviaState.airports,
    currencies: AppState.currencies,
    activedCurrency: AppPersistedState.activedCurrency
  });

  //Actions
  readonly #actions = createDispatchMap({
    updateFormValue: UpdateFormValue,
    setCurrency: appSetCurrency,
    setSellCurrency: appSetSellCurrency,
    getAirports: aviaGetAirports,
    resetSearchResult: aviaResetSearchResult,
    searchPackages: aviaSearchPackages
  });

  //Form Group
  readonly formGroup = new FormGroup({
    flightRequests: new FormArray<
      FormGroup<{
        arrivalLocation: FormControl<string | null>;
        arrivalType: FormControl<string | null>;
        departureDate: FormControl<Date | null>;
        departureLocation: FormControl<string | null>;
        departureType: FormControl<string | null>;
        returnDate: FormControl<Date | null>;
      }>
    >([]),
    adults: new FormControl<number>(1),
    children: new FormControl<number>(0),
    childrenAges: new FormArray<FormControl<number | null>>([]),
    _flightTicketType: new FormControl<FlightTicketTypeValues>('roundTrip'),
    ticketType: new FormControl<TicketTypesEnum | null>(TicketTypesEnum['Coach']),
    // the ticked classes (docs features/website/avia-class-multi-select R-CM1); `ticketType` always holds the first of them. A form saved
    // before 08.10.2026 has no list - it reads as [ticketType] (cabinClassList). R-C9 (website-web#82, Amiran 20.10.2026): Economy only by default;
    // a stored search restored by [ngxsForm] keeps the agent's own ticks
    ticketTypes: new FormControl<TicketTypesEnum[] | null>(DEFAULT_CABIN_CLASSES())
  });

  //FormValues (Signals)
  readonly formValues = {
    ticketType: toSignal(this.formGroup.controls.ticketType.valueChanges.pipe(map((e) => e ?? 0))),
    adults: toSignal(this.formGroup.controls.adults.valueChanges.pipe(map((e) => e ?? 0))),
    children: toSignal(this.formGroup.controls.children.valueChanges.pipe(map((e) => e ?? 0))),
    childrenAges: toSignal(this.formGroup.controls.childrenAges.valueChanges),
    firstFlightRequest: toSignal(this.formGroup.controls.flightRequests.valueChanges.pipe(map((e) => e[0] ?? null))),
    activedFlightType: toSignal(this.formGroup.controls._flightTicketType.valueChanges),
    ticketTypes: toSignal(
      this.formGroup.controls.ticketTypes.valueChanges.pipe(startWith(this.formGroup.controls.ticketTypes.value))
    )
  };

  /** R-CM1 / R-CM2: the classes the form will search, in the list order (one at least). */
  readonly cabinClasses = computed(() => {
    this.formValues.ticketType(); // re-read when the single class changes (a form saved before the list existed)
    return cabinClassList(this.formValues.ticketTypes(), this.formGroup.controls.ticketType.value);
  });

  /** R-CM1: the agent's ticks; `ticketType` follows the first so every one-class reader keeps working. Never searches (R-CM3). */
  setCabinClasses(classes: ReadonlyArray<TicketTypesEnum | string | null | undefined> | null | undefined) {
    const list = cabinClassList(classes);
    this.formGroup.controls.ticketTypes.setValue(list);
    this.formGroup.controls.ticketType.setValue(list[0]);
  }

  //Shortcuts
  get formStatus() {
    return this.#selectors.formStatus;
  }
  get hasChildrenAgesError() {
    if (!this.formGroup.controls.childrenAges.controls.length) return false;
    return this.formGroup.controls.childrenAges.controls.some((e) => e.errors);
  }
  get activedFlightType() {
    return this.formGroup.controls._flightTicketType.value;
  }
  set activedFlightType(value) {
    this.formGroup.controls._flightTicketType.setValue(value);
  }
  get flightRequests() {
    return this.formGroup.controls.flightRequests.controls;
  }
  get childrenAges() {
    return this.formGroup.controls.childrenAges.controls;
  }

  //Currency Selectotor helpers
  get activedCurrency() {
    return this.#selectors.activedCurrency;
  }
  get currencies() {
    return this.#selectors.currencies;
  }
  get onCurrencySelect() {
    return this.#actions.setCurrency;
  }

  //Airports Selector helpers
  readonly airportsTypedToken!: AirportTreeItem;
  get airports() {
    return this.#selectors.airports;
  }
  /** A search answer gets the same `_view` as the full tree; its counts are the catalogue's (airport picker R-1). */
  airportsSearchFn = (query?: string | null) => {
    return this.#catalogueApiService
      .getAirportTree(query)
      .pipe(map((nodes) => decorateAirportTree(nodes, this.airports()?.response)));
  };

  //Writable Values
  isInit = false;

  constructor() {
    // `returnDate` is required only for 'roundTrip'. The requiredIf validator runs on the returnDate
    // control itself, so it has to be re-run whenever the flight type changes (otherwise a stale
    // `required` error survives the switch to 'multipleFlights', where the field is not even rendered).
    this.formGroup.controls._flightTicketType.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.#revalidateReturnDates());
  }

  init() {
    this.#actions.setSellCurrency('avia', 'USD');

    if (this.isInit) return;
    this.#addFlightRequest();
    this.#initFormData();
    this.isInit = true;
  }
  #initFormData() {
    this.#actions.getAirports();

    const storedFormData = this.#selectors.searchFormData();

    // Restore the flight type first: it drives the returnDate validator of every flight request built below
    if (storedFormData?._flightTicketType) this.activedFlightType = storedFormData._flightTicketType;

    if (storedFormData?.childrenAges?.length)
      for (const iterator of storedFormData.childrenAges)
        this.formGroup.controls.childrenAges.push(new FormControl<number | null>(iterator, Validators.required));

    if (storedFormData?.flightRequests?.length) {
      this.formGroup.controls.flightRequests.clear();
      for (const iterator of storedFormData.flightRequests)
        this.formGroup.controls.flightRequests.push(this.#flightRequestForm(iterator));
    }
    // R-AVF4 (C33 F3, reload): a multi-city search stored before docs#439 may still carry a hidden return date; [ngxsForm] can write
    // it back after this, so aviaSearchPackages drops it from the request too (the guarantee)
    if (this.activedFlightType === 'multipleFlights') this.#clearReturnDates();
  }
  onAddChildren(count: number) {
    // R-BIH42 (website-web#84): never below 0 - removeAt on an empty array does nothing, so a negative count looped forever and hung the tab
    const children = Math.max(count ?? 0, 0);
    const current = this.formGroup.controls.childrenAges.length;
    if (current < children)
      for (let index = 0; index < children - current; index++)
        this.formGroup.controls.childrenAges.push(new FormControl<number | null>(null, Validators.required));
    else if (current > children)
      // R-HP-R3 (website-web#33): remove from the end - a climbing index over a shrinking array left stale age fields
      while (this.formGroup.controls.childrenAges.length > children)
        this.formGroup.controls.childrenAges.removeAt(this.formGroup.controls.childrenAges.length - 1);
  }
  onChangeFlightType(type: FlightTicketTypeValues): void {
    if (type === this.activedFlightType) return;
    if (type === 'multipleFlights') {
      if (this.formGroup.controls.flightRequests.controls[0].controls.returnDate.hasValidator(Validators.required))
        this.formGroup.controls.flightRequests.controls[0].controls.returnDate.removeValidators(Validators.required);
      this.onAddFlightRequest();
    } else {
      this.activedFlightType = type;
      const flightRequests = this.formGroup.controls.flightRequests;
      if (flightRequests.length > 1) {
        const storeFirstToKeep = flightRequests.controls[0];
        flightRequests.clear();
        flightRequests.push(storeFirstToKeep);
      }
      //updateValidators
      for (const element of flightRequests.controls) {
        if (type === 'roundTrip') element.controls.returnDate.addValidators(Validators.required);
        else {
          element.controls.returnDate.setValue(null);
          element.controls.returnDate.removeValidators(Validators.required);
        }
        element.controls.returnDate.updateValueAndValidity();
      }
    }
  }
  onAddFlightRequest() {
    if (this.activedFlightType !== 'multipleFlights') {
      // R-AVF4 (docs#439 point 1): "+" leaves Round Trip as One Way does - the hidden return date is emptied and not required,
      // otherwise it is sent and flights-api searches that leg as a round trip
      this.#clearReturnDates();
      this.activedFlightType = 'multipleFlights';
    }
    this.#addFlightRequest();
  }
  #clearReturnDates() {
    for (const flightRequest of this.formGroup.controls.flightRequests.controls) {
      flightRequest.controls.returnDate.setValue(null);
      flightRequest.controls.returnDate.removeValidators(Validators.required);
      flightRequest.controls.returnDate.updateValueAndValidity();
    }
  }
  #addFlightRequest() {
    if (this.isSubmited()) this.isSubmited.update(() => false);
    this.formGroup.controls.flightRequests.push(this.#flightRequestForm());
  }

  #revalidateReturnDates() {
    for (const flightRequest of this.formGroup.controls.flightRequests.controls)
      flightRequest.controls.returnDate.updateValueAndValidity();
  }

  #flightRequestForm(values?: FlightRequest) {
    return new FormGroup({
      arrivalLocation: new FormControl<string | null>(values?.arrivalLocation ?? null, Validators.required),
      arrivalType: new FormControl<string | null>(values?.arrivalType ?? null),
      departureDate: new FormControl<Date | null>(values?.departureDate ? new Date(values.departureDate) : null, [
        Validators.required,
        minDate(undefined, 'Date must be after today')
      ]),
      departureLocation: new FormControl<string | null>(values?.departureLocation ?? null, Validators.required),
      departureType: new FormControl<string | null>(values?.departureType ?? null),
      returnDate: new FormControl<Date | null>(values?.returnDate ? new Date(values.returnDate) : null, [
        requiredIf(this.formGroup.controls._flightTicketType, 'roundTrip'),
        minDateThanField('departureDate', 'Return Date must be after departure date')
      ])
    });
  }

  resetForm() {
    this.formGroup.controls.flightRequests.clear();
    this.formGroup.reset({
      flightRequests: [],
      adults: 1,
      children: 0,
      childrenAges: [],
      ticketType: TicketTypesEnum['Coach'],
      ticketTypes: DEFAULT_CABIN_CLASSES(),
      _flightTicketType: 'roundTrip'
    });

    this.#actions.updateFormValue({ path: this.ngxsForm, value: this.formGroup.getRawValue() });
    setTimeout(() => {
      this.#addFlightRequest();
      this.#actions.resetSearchResult();
    }, 100);
    this.isSubmited.update(() => false);
  }
  onSwapFlightRequestAirports(index: number) {
    //Store
    const fromValue = this.formGroup.controls.flightRequests.controls[index].controls.departureLocation.value;
    const toValue = this.formGroup.controls.flightRequests.controls[index].controls.arrivalLocation.value;
    //Swap
    this.formGroup.controls.flightRequests.controls[index].controls.departureLocation.setValue(toValue);
    this.formGroup.controls.flightRequests.controls[index].controls.arrivalLocation.setValue(fromValue);
  }
  onSubmit(showLoader = true) {
    // R-AVF12 (website-web#185): a second Search while the first runs is ignored - nothing written, no second loader
    if (this.formStatus().status === 'loading') return;
    if (!this.isSubmited()) this.isSubmited.update(() => true);
    if (this.formGroup.invalid) return;

    this.#actions.updateFormValue({ path: this.ngxsForm, value: this.formGroup.getRawValue() });

    this.#actions.searchPackages();
    this.isSubmited.update(() => false);
    if (showLoader) {
      this.#loadingWindowService.show();
      toObservable(this.formStatus, { injector: this.#injector })
        .pipe(
          filter(({ status }) => status !== 'loading'),
          take(1)
        )
        .subscribe(() => this.#loadingWindowService.hide());
    }
  }

  onRemoveFlighRequest(index: number) {
    this.formGroup.controls.flightRequests.removeAt(index);
  }

  dateRangeValidate(index: number) {
    if (this.activedFlightType === 'roundTrip') {
      const departureDate = this.formGroup.controls.flightRequests.controls[index].controls.departureDate.value;
      const returnDate = this.formGroup.controls.flightRequests.controls[index].controls.returnDate.value;

      const difference = departureDate && returnDate ? differenceInDays(returnDate, departureDate) : null;
      if (difference && difference <= 0)
        this.formGroup.controls.flightRequests.controls[index].controls.returnDate.setValue(null);
    }
  }
}
